import { useEffect, useMemo } from "react";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import { cn } from "utils";
import { uniq } from "lodash-es";
import { useSnapshot } from "valtio";
import { calculateChangeRate } from "../../../table/stock/columns/type";
import { EmptyState } from "./EmptyState";
import { HotItem } from "./HotItem";
import { LoadingState } from "./LoadingState";
import {
  Content,
  ContentCategoryEnum,
  OpenDataTypeEnum,
  StockSortField,
  ThemeItem,
} from "@features/api/openData";
import { SCOPE_MAP } from "@const/recommendQuestion";
import { ThemesScopeEnum } from "@const/market";
import { useAIInsightDetail } from "@hooks/useAIInsightDetail";
import { useGoToDetail } from "@hooks/useGoToDetail";
import { useIsPc } from "@hooks/useIsPc";
import { useRecommendQuestion } from "@hooks/useRecommendQuestion";
import { useThemesInfiniteQuery } from "@hooks/useThemesInfiniteQuery";

import { HOT_US_STOCK_THEME_SLUG } from "@componentsv1/radar/consts/themes";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@config/router";
import Translations from "@translate/components/Menu/MenuSearchModal/RecommendArea/HotStock";
import { favoritesCenter } from "@store/favorite";
import { rootStore } from "@store/rootStore";
import { useHotNews } from "@/src/hooks/useHotNews";
import { useStockDashboard } from "@/src/hooks/useStockDashboard";
import { useTokensByIds } from "@/src/hooks/useTokensByIds";

const I18n = I18nUtil.createScoped(Translations);

export const HotStock = ({
  onClose: _onClose,
  type,
}: {
  onClose: () => void;
  type?: "normal" | "portfolio";
}) => {
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const locale = I18nUtil.getCurrentLocale();
  const { onDetail, isLoading } = useGoToDetail("stock");
  const [isPc] = useIsPc();

  const { data } = useThemesInfiniteQuery({
    type: OpenDataTypeEnum.STOCK,
    limit: isPc ? 8 : 4,
    scope: ThemesScopeEnum.HotSearch,
    sortOrder: "ASC",
  });

  const hotThemes = data?.pages.flatMap((page) => page.rows);

  const {
    data: { data: stockData },
    isLoading: stockRequestAgentLoading,
  } = useStockDashboard({
    pageSize: 4,
    sortFiled: StockSortField.MARKET_CAP,
    gcTime: 1000 * 60,
    themeKey: HOT_US_STOCK_THEME_SLUG,
    enabled: type === "normal",
  });

  // 从 stockData 中提取每个股票的第一个 theme_id，去重后用于查询主题详情
  const stockThemeIds = useMemo(() => {
    if (type !== "normal" || !Array.isArray(stockData)) {
      return [] as string[];
    }
    const ids =
      stockData
        ?.map((item: any) => item?.theme_ids?.[0])
        ?.filter((id: unknown): id is string => typeof id === "string") ?? [];
    return uniq(ids);
  }, [stockData, type]);

  const queryParams = {
    [`scopes[${SCOPE_MAP.STOCK_PORTFOLIO_SEARCH}]`]: 6,
  };
  const { data: recommendData, isLoading: _recommendLoading } =
    useRecommendQuestion(queryParams, {
      enabled: type === "portfolio",
      staleTime: 1000 * 60 * 5,
    });
  const ids = useMemo(() => {
    if (!recommendData) {
      return [];
    }
    return uniq(
      Object.values(recommendData)
        .flat()
        .map((item: any) => item.id)
        .filter((id): id is string => !!id),
    );
  }, [recommendData]);
  const { data: tokensByIds, isLoading: tokensByIdsLoading } = useTokensByIds({
    ids,
    type: "stock",
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
  const portfolio = useMemo(() => {
    if (!recommendData || !Object.keys(recommendData)?.length) {
      return [];
    }
    if (!tokensByIds || !Object.keys(tokensByIds)?.length) {
      return [];
    }
    if (ids.length !== Object.keys(tokensByIds)?.length) {
      return [];
    }
    const portfolio = ids.map((id) => {
      if (!tokensByIds[id]) {
        return [];
      }
      return {
        ...tokensByIds[id],
      };
    });
    return portfolio;
  }, [tokensByIds, recommendData, ids]);

  const HotStock = useMemo(() => {
    if (type === "portfolio") {
      return portfolio;
    }
    return stockData;
  }, [portfolio, stockData, type]);

  useEffect(() => {
    if (!HotStock?.length) {
      return;
    }
    const ids = HotStock?.map((item: any) => item?.id);
    if (isLoggedIn && ownerInfo?.id) {
      favoritesCenter.checkBatchFavoritesStatus(ids, "STOCK");
    }
  }, [HotStock, ownerInfo?.id, isLoggedIn]);

  const recommendThemes =
    hotThemes
      ?.map((item: ThemeItem) => item.id)
      ?.filter((id) => typeof id === "string") || [];
  const { data: hotNews } = useHotNews({
    // enabled: type === "normal",
    enabled: false,
    initialParams: {
      locale: locale,
      limit: 4,
      offset: 0,
      themeIds: recommendThemes,
      category: ContentCategoryEnum.STOCK,
      symbols: [],
    },
  });

  // 从 hotNews 中提取每条新闻的第一个 theme_id，去重
  const newsThemeIds = useMemo(() => {
    if (type !== "normal" || !Array.isArray(hotNews)) {
      return [] as string[];
    }
    const ids =
      hotNews
        ?.map((item: Content) => item?.theme_ids?.[0])
        ?.filter((id: unknown): id is string => typeof id === "string") ?? [];
    return uniq(ids);
  }, [hotNews, type]);

  // 合并 stock 与 news 的 theme_id，统一一次批量查询（与上一改动方案一致，减少请求次数）
  const allThemeIds = useMemo(() => {
    return uniq([...stockThemeIds, ...newsThemeIds]);
  }, [stockThemeIds, newsThemeIds]);

  const { data: themesDetailList } = useAIInsightDetail({
    themeId: allThemeIds.join(","),
    type: OpenDataTypeEnum.STOCK,
    enabled: allThemeIds.length > 0 && type === "normal",
  });

  const themeMap = useMemo(() => {
    const map = new Map<string, ThemeItem>();
    (themesDetailList || []).forEach((theme) => {
      if (theme?.id) {
        map.set(theme.id, theme as ThemeItem);
      }
    });
    return map;
  }, [themesDetailList]);

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      {type === "normal" && (
        <div className="flex flex-col gap-3">
          <p className="md:title_s title_m text-content_tertiary">
            {I18n.t("Hot Themes")}
          </p>
          <div className="flex flex-wrap gap-3">
            {hotThemes?.map((item) => {
              const basePath =
                item.market === "US"
                  ? ROUTER_PATH.RADAR_US_STOCK
                  : ROUTER_PATH.RADAR_HK_STOCK;
              return (
                <ShellLink
                  aria-label="Hot Stock"
                  data-openreplay-label="home_hotstock_open"
                  id="lnk-hot-stock"
                  key={item.slug}
                  href={`${basePath}/${item.slug}`}
                  onClick={() => _onClose?.()}
                  className="px-2 py-[6px] rounded-md bg-background_white_overlay5 body_medium_s text-content_secondary hover:text-content_primary cursor-pointer"
                >
                  {item.theme}
                </ShellLink>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <p className="md:title_s title_m text-content_tertiary">
          {I18n.t("Hot Stocks")}
        </p>
        {(_recommendLoading ||
          stockRequestAgentLoading ||
          tokensByIdsLoading) && (
          <LoadingState
            count={type === "normal" ? 4 : 6}
            className={cn("grid-cols-1 md:grid-cols-2")}
          />
        )}
        {HotStock?.length > 0 && (
          <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
            {HotStock?.map((item: any) => {
              // 计算stock涨跌幅
              const calculatedResult = calculateChangeRate(
                item?.trading_data?.current_price,
                item?.trading_data?.previous_close,
              );
              const firstThemeId = item?.theme_ids?.[0];
              const matchedTheme = firstThemeId && themeMap.get(firstThemeId);
              const themeValue = matchedTheme || item?.themes?.[0] || undefined;
              return (
                <HotItem
                  {...item}
                  key={item.id}
                  image={item?.logo}
                  price={item?.trading_data?.current_price}
                  change={calculatedResult.value}
                  changClassname={calculatedResult.color}
                  name={item?.company_name || item?.name}
                  themeValue={themeValue}
                  type="stock"
                  originType={type}
                  onDetail={(record: any) => {
                    onDetail(record);
                    setTimeout(() => {
                      _onClose?.();
                    }, 1000);
                  }}
                  isLoading={isLoading}
                />
              );
            })}
          </div>
        )}
        {!_recommendLoading &&
          !stockRequestAgentLoading &&
          !tokensByIdsLoading &&
          type === "normal" &&
          HotStock?.length === 0 && <EmptyState />}
      </div>
    </div>
  );
};
